const DEFAULT_LOAD_TIMEOUT_MS = 10000;
const DEFAULT_LOGIN_TIMEOUT_MS = 10000;

function browserWindow() {
    return typeof window === "undefined" ? null : window;
}

function loginPayloadWithoutInvalidPhone(source) {
    const { phone, ...payload } = source;
    if (typeof phone !== "string") return payload;

    // Phone is optional. Strip display separators from international numbers,
    // but never guess a country code for a local number or include extensions.
    const normalized = phone.replace(/[\s().-]/g, "");
    if (/^\+[1-9]\d{1,14}$/.test(normalized)) payload.phone = normalized;
    return payload;
}

/**
 * Owns Tawk's reconnecting login lifecycle. Tawk login() refreshes the socket
 * session, so duplicate calls are observable in the agent dashboard rather
 * than being harmless repeated setters.
 */
export class TawkSessionController {
    constructor({
        getWindow = browserWindow,
        loadTimeoutMs = DEFAULT_LOAD_TIMEOUT_MS,
        loginTimeoutMs = DEFAULT_LOGIN_TIMEOUT_MS
    } = {}) {
        this.getWindow = getWindow;
        this.loadTimeoutMs = loadTimeoutMs;
        this.loginTimeoutMs = loginTimeoutMs;
        this.activeIdentityKey = null;
        this.pendingLogin = null;
        this.pendingLogout = null;
        this.readyPromise = null;
    }

    waitUntilReady(onLoad) {
        const targetWindow = this.getWindow();
        if (!targetWindow) return Promise.resolve(null);
        if (typeof targetWindow.Tawk_API?.login === "function") {
            return Promise.resolve(targetWindow.Tawk_API);
        }
        if (this.readyPromise) return this.readyPromise;

        this.readyPromise = new Promise((resolve) => {
            let settled = false;
            const finish = () => {
                if (settled) return;
                settled = true;
                targetWindow.clearTimeout(timeout);
                const api = targetWindow.Tawk_API;
                resolve(typeof api?.login === "function" ? api : null);
            };
            const timeout = targetWindow.setTimeout(finish, this.loadTimeoutMs);

            if (typeof onLoad === "function") {
                onLoad(finish);
            } else {
                targetWindow.addEventListener("tawkLoad", finish, { once: true });
            }
        }).finally(() => {
            this.readyPromise = null;
        });

        return this.readyPromise;
    }

    ensureLoggedIn({ identityKey, loadPayload, onLoad, afterLogin }) {
        if (!identityKey || typeof loadPayload !== "function") return Promise.resolve(false);
        if (this.activeIdentityKey === identityKey) return Promise.resolve(true);
        if (this.pendingLogin?.identityKey === identityKey) return this.pendingLogin.promise;
        if (this.pendingLogout) {
            return this.pendingLogout.then(() =>
                this.ensureLoggedIn({ identityKey, loadPayload, onLoad, afterLogin })
            );
        }
        if (this.activeIdentityKey && this.activeIdentityKey !== identityKey) {
            return this.logout().then(() =>
                this.ensureLoggedIn({ identityKey, loadPayload, onLoad, afterLogin })
            );
        }

        // A real identity change must follow the current transition rather than
        // racing two reconnecting login() calls against the same widget.
        if (this.pendingLogin) {
            return this.pendingLogin.promise.then(() =>
                this.ensureLoggedIn({ identityKey, loadPayload, onLoad, afterLogin })
            );
        }

        const operation = (async () => {
            const source = await loadPayload();
            if (!source?.userId || !source?.hash) return false;
            let payload = loginPayloadWithoutInvalidPhone(source);

            const api = await this.waitUntilReady(onLoad);
            if (!api) return false;
            if (this.activeIdentityKey === identityKey) return true;

            const loggedIn = await new Promise((resolve) => {
                let settled = false;
                const finish = (success) => {
                    if (settled) return;
                    settled = true;
                    this.getWindow()?.clearTimeout(timeout);
                    resolve(success);
                };
                const timeout = this.getWindow()?.setTimeout(
                    () => finish(false),
                    this.loginTimeoutMs
                );
                const login = () => {
                    let callbackCalled = false;
                    api.login(payload, (error) => {
                        if (settled || callbackCalled) return;
                        callbackCalled = true;
                        if (error?.message === "INVALID_PHONE" && payload.phone) {
                            // Tawk can reject a structurally valid number. Retry
                            // once with the same signed identity, without phone.
                            payload = { ...payload };
                            delete payload.phone;
                            login();
                            return;
                        }
                        if (error) {
                            console.error("Tawk login", error);
                            finish(false);
                            return;
                        }
                        finish(true);
                    });
                };
                login();
            });
            if (!loggedIn) return false;

            this.activeIdentityKey = identityKey;
            if (typeof afterLogin === "function") {
                await afterLogin(api, payload);
            }
            return true;
        })();

        this.pendingLogin = { identityKey, promise: operation };
        const clearPendingLogin = () => {
            if (this.pendingLogin?.promise === operation) this.pendingLogin = null;
        };
        operation.then(clearPendingLogin, clearPendingLogin);
        return operation;
    }

    logout() {
        if (this.pendingLogout) return this.pendingLogout;

        const operation = (async () => {
            const pending = this.pendingLogin?.promise;
            if (pending) await pending.catch(() => {});

            this.activeIdentityKey = null;
            const api = this.getWindow()?.Tawk_API;
            if (typeof api?.logout !== "function") return;

            await new Promise((resolve) => {
                let settled = false;
                const finish = (error) => {
                    if (settled) return;
                    settled = true;
                    this.getWindow()?.clearTimeout(timeout);
                    if (error) console.error("Tawk logout", error);
                    resolve();
                };
                const timeout = this.getWindow()?.setTimeout(() => finish(), 1500);
                try {
                    api.logout(finish);
                } catch (error) {
                    finish(error);
                }
            });
        })();

        this.pendingLogout = operation;
        const clearPendingLogout = () => {
            if (this.pendingLogout === operation) this.pendingLogout = null;
        };
        operation.then(clearPendingLogout, clearPendingLogout);
        return operation;
    }
}

export const tawkSession = new TawkSessionController();
